import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Building2, CheckCircle, Clock, AlertCircle, ArrowLeft, Search,
  Send, Shield, FileText, Calendar, Loader2, ExternalLink,
  ChevronRight, AlertTriangle, Zap, Globe, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to safely render details
const renderDetails = (details) => {
  if (details === null || details === undefined) return '';
  if (typeof details === 'string') return details;
  if (typeof details === 'number') return String(details);
  if (typeof details === 'object') {
    // Handle specific known objects
    if (details.siren !== undefined) {
      return `SIREN: ${details.siren || '-'}, SIRET: ${details.siret || '-'}`;
    }
    if (details.ligne1 !== undefined) {
      return `${details.ligne1 || ''}, ${details.code_postal || ''} ${details.ville || ''}`;
    }
    // Generic object handling
    return Object.entries(details)
      .filter(([_, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ');
  }
  return String(details);
};

const PPFPage = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchSiren, setSearchSiren] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [statusRes, dashboardRes, readinessRes] = await Promise.all([
        fetch(`${API_URL}/api/ppf/status`, { headers }),
        fetch(`${API_URL}/api/ppf/dashboard`, { headers }),
        fetch(`${API_URL}/api/ppf/readiness`, { headers })
      ]);

      if (statusRes.ok) setStatus(await statusRes.json());
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (readinessRes.ok) setReadiness(await readinessRes.json());
    } catch (error) {
      console.error('Error loading PPF data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchAnnuaire = async () => {
    if (!searchSiren.trim()) return;
    
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/ppf/annuaire/search?siren=${searchSiren.trim()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResult(data);
      } else {
        toast({
          title: 'Erreur',
          description: 'Entreprise non trouvée',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la recherche',
        variant: 'destructive'
      });
    } finally {
      setSearching(false);
    }
  };

  const getChecklistIcon = (status) => {
    if (status === 'complete') return <CheckCircle className="h-5 w-5 text-emerald-600" />;
    if (status === 'warning') return <AlertTriangle className="h-5 w-5 text-amber-600" />;
    if (status === 'pending') return <Clock className="h-5 w-5 text-emerald-600" />;
    return <AlertCircle className="h-5 w-5 text-red-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ppf-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-emerald-600" />
              Portail Public de Facturation
            </h1>
            <p className="text-slate-500 text-sm">Connexion officielle à l'État français</p>
          </div>
        </div>
        <Badge className={status?.simulation_mode ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
          {status?.simulation_mode ? 'Mode Simulation' : 'Production'}
        </Badge>
      </div>

      {/* PPF Status Banner */}
      <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-lime-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-2xl bg-white shadow-sm">
                <Shield className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  SmartFacture AI est prêt pour le PPF
                </h2>
                <p className="text-slate-600 mt-1">
                  Le Portail Public de Facturation ouvrira le <strong>1er septembre 2026</strong>
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Score de préparation</p>
              <p className="text-3xl font-bold text-emerald-600">
                {readiness?.readiness_score?.toFixed(0) || 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      {dashboard && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Factures soumises</p>
                  <p className="text-2xl font-bold text-slate-800">{dashboard.stats?.total_submitted || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50">
                  <Send className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">En simulation</p>
                  <p className="text-2xl font-bold text-amber-600">{dashboard.stats?.in_simulation || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Prêtes à soumettre</p>
                  <p className="text-2xl font-bold text-emerald-600">{dashboard.stats?.ready_to_submit || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Non conformes</p>
                  <p className="text-2xl font-bold text-red-600">{dashboard.stats?.not_compliant || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-red-50">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Readiness Checklist */}
      {readiness && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-emerald-600" />
              Checklist de conformité PPF
            </CardTitle>
            <CardDescription>
              Vérifiez que tout est prêt pour le lancement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {readiness.checklist?.map((item, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-4 rounded-xl border ${
                    item.status === 'complete' ? 'border-emerald-200 bg-emerald-50/50' :
                    item.status === 'warning' ? 'border-amber-200 bg-amber-50/50' :
                    item.status === 'pending' ? 'border-emerald-200 bg-emerald-50/50' :
                    'border-red-200 bg-red-50/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {getChecklistIcon(item.status)}
                    <div>
                      <p className="font-medium text-slate-800">{item.item}</p>
                      <p className="text-sm text-slate-500">
                        {renderDetails(item.details)}
                      </p>
                    </div>
                  </div>
                  <Badge className={
                    item.status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                    item.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                    item.status === 'pending' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-red-100 text-red-700'
                  }>
                    {item.status === 'complete' ? 'Validé' :
                     item.status === 'warning' ? 'Attention' :
                     item.status === 'pending' ? 'En attente' : 'À corriger'}
                  </Badge>
                </div>
              ))}
            </div>

            {/* Next Steps */}
            {readiness.next_steps && (
              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-700 mb-2">Prochaines étapes :</p>
                <ul className="space-y-1">
                  {readiness.next_steps.map((step, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Annuaire Search */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5 text-emerald-600" />
            Recherche dans l'Annuaire PPF
          </CardTitle>
          <CardDescription>
            Vérifiez si une entreprise est enregistrée dans l'annuaire officiel
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              value={searchSiren}
              onChange={(e) => setSearchSiren(e.target.value)}
              placeholder="Entrez un numéro SIREN (9 chiffres)"
              className="flex-1"
              maxLength={9}
              data-testid="siren-search"
            />
            <Button 
              onClick={handleSearchAnnuaire}
              disabled={searching || searchSiren.length !== 9}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {searching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>

          {searchResult && (
            <div className="mt-4 p-4 bg-slate-50 rounded-xl">
              {searchResult.simulation && (
                <Badge className="bg-amber-100 text-amber-700 mb-3">Mode Simulation</Badge>
              )}
              {searchResult.results?.map((company, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-slate-800">{company.denomination}</h4>
                    {company.ppf_activated && (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        PPF Activé
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">SIREN</p>
                      <p className="font-medium text-slate-700">{company.siren}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">SIRET</p>
                      <p className="font-medium text-slate-700">{company.siret}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-slate-500">Adresse</p>
                      <p className="font-medium text-slate-700">
                        {company.adresse 
                          ? `${company.adresse.ligne1 || ''}, ${company.adresse.code_postal || ''} ${company.adresse.ville || ''}`
                          : '-'
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Mode de routage</p>
                      <p className="font-medium text-slate-700">{company.routing_preference || 'PPF Direct'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Submissions */}
      {dashboard?.recent_submissions?.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Dernières soumissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.recent_submissions.map((sub, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 border border-slate-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-800">{sub.invoice_number}</p>
                      <p className="text-sm text-slate-500">{sub.client_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-slate-500">PPF ID</p>
                      <p className="text-sm font-mono text-slate-700">{sub.ppf_id}</p>
                    </div>
                    <Badge className={
                      sub.ppf_status === 'deposee' ? 'bg-emerald-100 text-emerald-700' :
                      sub.ppf_status === 'approuvee' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {sub.ppf_status}
                    </Badge>
                    {sub.ppf_simulation && (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">SIM</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <Calendar className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-800">Calendrier de déploiement</h3>
              <ul className="mt-2 space-y-1 text-sm text-emerald-700">
                <li>• <strong>1er septembre 2026</strong> : Grandes entreprises (CA &gt; 250M€)</li>
                <li>• <strong>1er septembre 2027</strong> : ETI (CA entre 50M€ et 250M€)</li>
                <li>• <strong>1er septembre 2028</strong> : PME et micro-entreprises</li>
              </ul>
              <a 
                href="https://www.impots.gouv.fr/e-invoicing" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-3 text-sm text-emerald-600 hover:underline"
              >
                En savoir plus sur le site officiel
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PPFPage;
